import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, LineChart, Line, Legend,
  PieChart, Pie,
} from 'recharts';
import { useSOCCache } from '../../hooks/useSOCCache';

const S = {
  panel: { background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '14px 16px' },
  label: { fontSize: 11, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '.08em', marginBottom: 10 },
};

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1e2a', border: '1px solid #ffffff22', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#e8eaf0' }}>
      {label && <div style={{ color: '#6b7280', marginBottom: 4 }}>{label}</div>}
      {payload.map((p, i) => <div key={i} style={{ color: p.color || '#e8eaf0' }}>{p.name}: <b>{p.value}</b></div>)}
    </div>
  );
}

const ATTACK_COLORS = {
  'APT-Reconnaissance':       '#a78bfa',
  'APT-Lateral-Movement':     '#38bdf8',
  'APT-Persistence':          '#f472b6',
  'APT-Privilege-Escalation': '#fb923c',
  'APT-Defense-Evasion':      '#facc15',
  'APT-C2-Communication':     '#4ade80',
  'APT-Data-Exfiltration':    '#f87171',
  'APT-Living-Off-The-Land':  '#e879f9',
};

function getHeatColor(val, max) {
  if (!max || val === 0) return '#0d1018';
  const r = val / max;
  if (r > 0.8) return '#ff3b4e';
  if (r > 0.6) return '#f97316';
  if (r > 0.4) return '#f59e0b';
  if (r > 0.2) return '#10b981';
  return '#134e4a';
}

function parseHour(ts) {
  if (!ts) return -1;
  try {
    const m = String(ts).match(/(\d{1,2}):(\d{2})$/);
    if (m) return parseInt(m[1], 10);
    return new Date(ts).getHours();
  } catch { return -1; }
}

function CustomPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace' }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export default function Analytics() {
  // ── Single cache hook — data persists across sign-out/sign-in ──────────────
  const { alerts, metrics, loading, refresh } = useSOCCache();

  // ── Heatmap drill-down state ──────────────────────────────────────────────
  const [drillHour, setDrillHour] = useState(null);

  // ── Derived data ──────────────────────────────────────────────────────────
  const hourlyBuckets = Array(24).fill(0);
  if (metrics?.hourly_distribution) {
    for (let h = 0; h < 24; h++) {
      hourlyBuckets[h] = metrics.hourly_distribution[h] ?? metrics.hourly_distribution[String(h)] ?? 0;
    }
  } else {
    alerts.forEach(a => {
      const h = parseHour(a.timestamp);
      if (h >= 0 && h < 24) hourlyBuckets[h]++;
    });
  }
  const maxHourly = Math.max(...hourlyBuckets, 1);

  const confBuckets = Array(10).fill(0);
  alerts.forEach(a => {
    const raw    = a.confidence ?? 0;
    const pct    = raw <= 1 ? raw * 100 : raw;
    const bucket = Math.min(Math.floor(pct / 10), 9);
    confBuckets[bucket]++;
  });
  const confData = confBuckets.map((count, i) => ({
    range: `${i * 10}`,
    count,
    fill: i >= 8 ? '#ff3b4e' : i >= 6 ? '#f59e0b' : '#10b981',
  }));

  const attackTypeData = (metrics?.attack_types || [])
    .slice(0, 8)
    .map(d => ({ type: d.type.replace('APT-', ''), fullType: d.type, count: d.count }));
  const pieData = (metrics?.attack_types || [])
    .map(d => ({ name: d.type.replace('APT-', ''), fullName: d.type, value: d.count }));

  const now = new Date();
  const sevTimeline = Array.from({ length: 12 }, (_, i) => {
    const targetHour = (now.getHours() - 11 + i + 24) % 24;
    const bucket = alerts.filter(a => parseHour(a.timestamp) === targetHour);
    return {
      hour: `${String(targetHour).padStart(2,'0')}:00`,
      CRITICAL: bucket.filter(a => (a.severity || a.threat_level || '').toUpperCase() === 'CRITICAL').length,
      HIGH:     bucket.filter(a => (a.severity || a.threat_level || '').toUpperCase() === 'HIGH').length,
      MEDIUM:   bucket.filter(a => (a.severity || a.threat_level || '').toUpperCase() === 'MEDIUM').length,
      LOW:      bucket.filter(a => (a.severity || a.threat_level || '').toUpperCase() === 'LOW').length,
    };
  });

  const aptAlerts    = alerts.filter(a => (a.prediction || '').toUpperCase() === 'APT');
  const benignAlerts = alerts.filter(a => (a.prediction || '').toUpperCase() !== 'APT');
  const avgConf      = aptAlerts.length
    ? (aptAlerts.reduce((s, a) => s + (a.confidence || 0), 0) / aptAlerts.length * 100).toFixed(1)
    : '0.0';

  const sevCount = {
    CRITICAL: metrics?.severity_distribution?.CRITICAL || 0,
    HIGH:     metrics?.severity_distribution?.HIGH     || 0,
    MEDIUM:   metrics?.severity_distribution?.MEDIUM   || 0,
    LOW:      metrics?.severity_distribution?.LOW      || 0,
  };

  // ── Loading state — shows stale data immediately if cache exists ──────────
  if (loading && alerts.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <div style={{ fontSize: 12, color: '#374151', fontFamily: 'JetBrains Mono, monospace' }}>
          Loading analytics...
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        {[
          { l: 'TOTAL APT ALERTS',      v: (metrics?.total_attacks || aptAlerts.length).toLocaleString(), c: '#3b82f6' },
          { l: 'TOTAL DATASET FLOWS',   v: ((metrics?.total_attacks || 0) + (metrics?.total_normal || 0)).toLocaleString(), c: '#ff3b4e' },
          { l: 'BENIGN / NORMAL',       v: (metrics?.total_normal || 0).toLocaleString(), c: '#10b981' },
          { l: 'AVG CONFIDENCE',        v: `${avgConf}%`,                  c: '#f59e0b' },
        ].map(({ l, v, c }) => (
          <div key={l} style={{ ...S.panel, borderTop: `2px solid ${c}` }}>
            <div style={{ fontSize: 10, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '.08em', marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color: c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Heatmap with drill-down */}
      <div style={S.panel}>
        <div style={S.label}>HOURLY ALERT HEATMAP (24H) — click a cell to drill down</div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
          {hourlyBuckets.map((count, h) => (
            <div key={h} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div
                title={`${h}:00 — ${count} alerts`}
                onClick={() => setDrillHour(drillHour === h ? null : h)}
                style={{
                  width: '100%', height: 52,
                  background: getHeatColor(count, maxHourly),
                  borderRadius: 4,
                  border: drillHour === h ? '2px solid #3b82f6' : '1px solid #ffffff08',
                  cursor: 'pointer',
                  transition: 'border 0.15s',
                }}
              />
              <div style={{ fontSize: 9, color: '#4b5563', fontFamily: 'JetBrains Mono, monospace' }}>{h}</div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 12, marginTop: 10, justifyContent: 'flex-end' }}>
          {[['None','#0d1018'],['Low','#134e4a'],['Med','#f59e0b'],['High','#f97316'],['Peak','#ff3b4e']].map(([l, c]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace' }}>
              <div style={{ width: 10, height: 10, background: c, borderRadius: 2 }} />{l}
            </div>
          ))}
        </div>

        {/* Drill-down panel */}
        {drillHour !== null && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#0d1018', borderRadius: 6, border: '1px solid #3b82f622' }}>
            <div style={{ fontSize: 10, color: '#60a5fa', fontFamily: 'JetBrains Mono, monospace', marginBottom: 8 }}>
              DRILL-DOWN: {String(drillHour).padStart(2,'0')}:00 — {hourlyBuckets[drillHour]} events
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
              {Object.keys(ATTACK_COLORS).map(type => {
                const count = alerts.filter(a => parseHour(a.timestamp) === drillHour && a.attack_type === type).length;
                if (!count) return null;
                return (
                  <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: '#141720', borderRadius: 4, fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}>
                    <span style={{ color: '#9ca3af' }}>{type.replace('APT-', '')}</span>
                    <span style={{ color: ATTACK_COLORS[type] }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Confidence + Top attack types */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={S.panel}>
          <div style={S.label}>CONFIDENCE SCORE DISTRIBUTION</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={confData} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" />
              <XAxis dataKey="range" tick={{ fill: '#6b7280', fontSize: 9 }} tickLine={false} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 9 }} tickLine={false} axisLine={false} />
              <Tooltip content={<TT />} />
              <Bar dataKey="count" name="Alerts" radius={[3, 3, 0, 0]}>
                {confData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: 12, marginTop: 6, justifyContent: 'center' }}>
            {[['Low (0-59%)', '#10b981'], ['Med (60-79%)', '#f59e0b'], ['High (80%+)', '#ff3b4e']].map(([l, c]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace' }}>
                <div style={{ width: 8, height: 8, background: c, borderRadius: 2 }} />{l}
              </div>
            ))}
          </div>
        </div>

        <div style={S.panel}>
          <div style={S.label}>TOP ATTACK TYPES</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={attackTypeData} layout="vertical" margin={{ top: 0, right: 40, bottom: 0, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis dataKey="type" type="category"
                tick={{ fill: '#9ca3af', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}
                tickLine={false} width={110} />
              <Tooltip content={<TT />} />
              <Bar dataKey="count" name="Count" radius={[0, 3, 3, 0]}>
                {attackTypeData.map((d, i) => <Cell key={i} fill={ATTACK_COLORS[d.fullType] || '#6366f1'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Severity trend + Attack type share */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 12 }}>
        <div style={S.panel}>
          <div style={S.label}>SEVERITY TREND (LAST 12H)</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={sevTimeline} margin={{ top: 4, right: 8, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" />
              <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 10 }} tickLine={false} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={<TT />} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#9ca3af' }} />
              <Line type="monotone" dataKey="CRITICAL" stroke="#ff3b4e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="HIGH"     stroke="#f97316" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="MEDIUM"   stroke="#f59e0b" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="LOW"      stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={S.panel}>
          <div style={S.label}>ATTACK TYPE SHARE</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ResponsiveContainer width={150} height={150}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={65}
                  dataKey="value" labelLine={false} label={CustomPieLabel}>
                  {pieData.map((d, i) => <Cell key={i} fill={ATTACK_COLORS[d.fullName] || '#6366f1'} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1a1e2a', border: '1px solid #ffffff22', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {pieData.map(d => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: ATTACK_COLORS[d.fullName] || '#6366f1' }} />
                  <span style={{ fontSize: 9, color: '#9ca3af', fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>{d.name}</span>
                  <span style={{ fontSize: 9, color: '#e8eaf0', fontFamily: 'JetBrains Mono, monospace' }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #ffffff0d', marginTop: 10, paddingTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {[['CRITICAL', '#ff3b4e'], ['HIGH', '#f97316'], ['MEDIUM', '#f59e0b'], ['LOW', '#10b981']].map(([s, c]) => (
              <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 9, color: c, fontFamily: 'JetBrains Mono, monospace' }}>{s}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: 'JetBrains Mono, monospace' }}>{sevCount[s]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
